import { InfoTooltip } from '@antimatterhq/ui/components/infoTooltip';
import { Input } from '@antimatterhq/ui/components/input';
import {
  RadioGroup,
  RadioGroupProps,
  RadioGroupItem,
} from '@antimatterhq/ui/components/radioGroup';
import { useFormik, FormikErrors, FormikTouched } from 'formik';
import { Text } from '@antimatterhq/ui/components/typography';
import { Label } from '@antimatterhq/ui/components/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@antimatterhq/ui/components/select';
import { ProcessingScheduleSchemaFormValues } from '@/zod/schemas/ProcessingScheduleSchema';
import {
  scheduleAtLeastEveryDs,
  scheduleExactlyAtDs,
} from '@/strings/StringsTooltips';
import { FormCronInput } from '@/components/form/cron/FormCronInput';

export type ProcessingScheduleRadioGroupProps = {
  handleChange: ReturnType<typeof useFormik>['handleChange'];
  handleBlur: ReturnType<typeof useFormik>['handleBlur'];
  values: ProcessingScheduleSchemaFormValues;
  errors: FormikErrors<ProcessingScheduleSchemaFormValues>;
  touched: FormikTouched<ProcessingScheduleSchemaFormValues>;
} & Pick<RadioGroupProps, 'disabled'>;

export type DurationUnit = 'm' | 'h' | 'd';

const durationUnitOptions: { value: DurationUnit; label: string }[] = [
  { value: 'm', label: 'Minutes' },
  { value: 'h', label: 'Hours' },
  { value: 'd', label: 'Days' },
];

export default function ProcessingScheduleRadioGroup({
  disabled,
  handleChange,
  handleBlur,
  values,
  errors,
  touched,
}: ProcessingScheduleRadioGroupProps) {
  return (
    <RadioGroup
      id="schedule"
      name="schedule"
      value={values.schedule}
      onValueChange={(value) =>
        handleChange({ target: { name: 'schedule', value } })
      }
      disabled={disabled}
      data-testid="processing-schedule-radio-group"
    >
      <div
        className="flex items-center space-x-2"
        data-testid="duration-radio-container"
      >
        <div
          className="flex w-32 items-center space-x-2"
          data-testid="duration-radio-label-container"
        >
          <RadioGroupItem
            value="duration"
            id="duration-radio"
            data-testid="duration-radio-item"
            aria-labelledby="duration-radio-label"
          />
          <Label
            htmlFor="duration-radio"
            id="duration-radio-label"
            className="text-grey-800 dark:text-grey-100"
            data-testid="duration-radio-label"
            mb={0}
          >
            At least every:
          </Label>
        </div>
        <div
          className="flex flex-row items-start gap-2"
          data-testid="duration-input-container"
        >
          <Input
            id="duration"
            name="duration"
            type="number"
            min={0}
            value={values.duration === null ? '' : values.duration}
            disabled={values.schedule !== 'duration' || disabled}
            onChange={(e) =>
              handleChange({
                target: {
                  name: 'duration',
                  value: e.target.value === '' ? null : Number(e.target.value),
                },
              })
            }
            onBlur={() => handleBlur({ target: { name: 'duration' } })}
            className="w-[200px]"
            data-testid="duration-input"
          />
          <div className="w-24" data-testid="duration-unit-select-container">
            <Select
              name="durationUnit"
              defaultValue="m"
              value={values.durationUnit}
              onValueChange={(value) =>
                handleChange({ target: { name: 'durationUnit', value } })
              }
              disabled={!values.duration}
              data-testid="duration-unit-select"
            >
              <SelectTrigger
                onBlur={() => handleBlur({ target: { name: 'durationUnit' } })}
                data-testid="duration-unit-select"
              >
                <SelectValue
                  placeholder="Time unit"
                  data-testid="duration-unit-select-value"
                >
                  {durationUnitOptions.find(
                    (unit) => unit.value === values.durationUnit,
                  )?.label || 'Select a unit'}
                </SelectValue>
                <SelectContent data-testid="duration-unit-select-content">
                  {durationUnitOptions.map((unit) => (
                    <SelectItem
                      key={unit.value}
                      value={unit.value}
                      data-testid={`duration-unit-select-item-${unit.value}`}
                    >
                      <Text
                        data-testid={`duration-unit-select-text-${unit.value}`}
                      >
                        {unit.label}
                      </Text>
                    </SelectItem>
                  ))}
                </SelectContent>
              </SelectTrigger>
            </Select>
          </div>
        </div>
        <InfoTooltip
          content={scheduleAtLeastEveryDs}
          data-testid="duration-info-tooltip"
        />
      </div>
      {!!errors.duration && touched.duration && (
        <div className="pl-32" data-testid="duration-error-container">
          <Text
            size="sm"
            className="text-red-500"
            data-testid="duration-error-text"
          >
            {errors.duration}
          </Text>
        </div>
      )}

      <div
        className="flex items-center space-x-2"
        data-testid="cron-radio-container"
      >
        <div
          className="flex w-32 items-center space-x-2"
          data-testid="cron-radio-label-container"
        >
          <RadioGroupItem
            value="cron"
            id="cron-radio"
            data-testid="cron-radio-item"
            aria-labelledby="cron-radio-label"
          />
          <Label
            htmlFor="cron-radio"
            id="cron-radio-label"
            className="w-32 text-grey-800 dark:text-grey-100"
            data-testid="cron-radio-label"
            mb={0}
          >
            Exactly at
          </Label>
        </div>
        <div data-testid="cron-input-container">
          <FormCronInput
            name="cron"
            value={values.cron ?? ''}
            disabled={values.schedule !== 'cron' || disabled}
            onChange={handleChange}
            data-testid="cron-input"
          />
        </div>
        <InfoTooltip
          content={scheduleExactlyAtDs}
          interactive
          data-testid="cron-info-tooltip"
        />
      </div>
      <div
        className="flex items-center space-x-2"
        data-testid="continuous-radio-container"
      >
        <RadioGroupItem
          value="continuous"
          id="continuous-radio"
          data-testid="continuous-radio-item"
          aria-labelledby="continuous-radio-label"
        />
        <Label
          htmlFor="continuous-radio"
          id="continuous-radio-label"
          className="text-grey-800 dark:text-grey-100"
          data-testid="continuous-radio-label"
          mb={0}
        >
          Continuous
        </Label>
        <div className="h-[40px]" data-testid="continuous-spacer"></div>
      </div>
    </RadioGroup>
  );
}
